(() => {
  const KEY = "compare_products_v2";

  /*
    Old category lock keys.
    We keep these only to clear old saved restriction data.
    New code allows products from multiple categories.
  */
  const CAT_KEY = "compare_category_v1";
  const CAT_TITLE_KEY = "compare_category_title_v1";

  const MAX = 4;
  const COMPARE_URL = "/pages/compare";
  const OPTION_FALLBACK_TEXT = "variants";

  const read = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(KEY) || "[]");

      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => {
          if (typeof item === "string") {
            return {
              handle: item,
              category: "",
              categoryTitle: "",
              specs: []
            };
          }

          return {
            handle: item?.handle || "",
            category: item?.category || "",
            categoryTitle: item?.categoryTitle || item?.category || "",
            specs: Array.isArray(item?.specs) ? item.specs : []
          };
        })
        .filter((item) => item.handle);
    } catch (e) {
      return [];
    }
  };

  const write = (arr) => {
    localStorage.setItem(KEY, JSON.stringify(Array.isArray(arr) ? arr : []));
  };

  const getHandles = () => read().map((item) => item.handle).filter(Boolean);

  function clearLegacyCategoryLock() {
    localStorage.removeItem(CAT_KEY);
    localStorage.removeItem(CAT_TITLE_KEY);
  }

  function isComparePage() {
    const path = window.location.pathname.replace(/\/$/, "");
    return path === COMPARE_URL || !!document.querySelector("[data-compare-page]");
  }

  async function fetchProduct(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async function fetchProductType(handle) {
    const p = await fetchProduct(handle);
    return p && p.type ? String(p.type) : "";
  }

  async function fetchProductSpecsFromPage(handle) {
    try {
      const res = await fetch(`/products/${handle}`);
      if (!res.ok) return [];

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const el = doc.querySelector('[id^="ProductSpecs-"]');

      if (!el) return [];

      const parsed = JSON.parse(el.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  async function fetchCollectionProducts(url) {
    try {
      const res = await fetch(url);
      if (!res.ok) return [];

      const data = await res.json();

      if (Array.isArray(data)) return data;
      if (Array.isArray(data.products)) return data.products;

      return [];
    } catch (e) {
      return [];
    }
  }

  function getCurrentProductSpecs() {
    const el = document.querySelector('[id^="ProductSpecs-"]');
    if (!el) return [];

    try {
      const parsed = JSON.parse(el.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function getStoredCompareItem(handle) {
    return read().find((item) => item.handle === handle) || null;
  }

  function getCollectionProductImage(product) {
    if (!product) return "";

    if (typeof product.featured_image === "string") {
      return product.featured_image;
    }

    if (product.featured_image && typeof product.featured_image.src === "string") {
      return product.featured_image.src;
    }

    if (typeof product.image === "string") {
      return product.image;
    }

    if (product.image && typeof product.image.src === "string") {
      return product.image.src;
    }

    if (Array.isArray(product.images) && product.images.length) {
      const firstImage = product.images[0];

      if (typeof firstImage === "string") {
        return firstImage;
      }

      if (firstImage && typeof firstImage.src === "string") {
        return firstImage.src;
      }
    }

    return "";
  }

  function getCollectionProductBrand(product) {
    return product?.vendor || product?.brand || product?.product_vendor || "No brand";
  }

  function ensureProductPillMarkup(pill) {
    if (!pill) return;

    const hasImage = pill.querySelector("[data-product-pill-image]");
    const hasTitle = pill.querySelector("[data-product-pill-title]");
    const hasBrand = pill.querySelector("[data-product-pill-brand]");

    if (hasImage && hasTitle && hasBrand) return;

    pill.innerHTML = `
      <span class="compare-product-pill-image-wrap">
        <img
          class="compare-product-pill-image"
          data-product-pill-image
          src=""
          alt=""
          width="160"
          height="160"
          loading="lazy"
        >
      </span>

      <span class="compare-product-pill-content">
        <span class="compare-product-pill-text" data-product-pill-title></span>
        <span class="compare-product-pill-brand" data-product-pill-brand></span>
      </span>
    `;
  }

  function updateBadges() {
    const count = read().length;

    document.querySelectorAll("[data-compare-count]").forEach((el) => {
      el.textContent = String(count);

      if (count === 0) {
        el.setAttribute("hidden", "");
      } else {
        el.removeAttribute("hidden");
      }
    });
  }

  function syncCheckboxes() {
    const list = getHandles();

    document.querySelectorAll('input[data-compare-add]').forEach((cb) => {
      const h = cb.getAttribute("data-compare-handle") || "";
      cb.checked = list.includes(h);
    });
  }

  function syncCompareButtons() {
    const list = getHandles();

    document.querySelectorAll("[data-compare-add]").forEach((btn) => {
      const handle = btn.getAttribute("data-compare-handle");
      if (!handle) return;

      const added = list.includes(handle);

      btn.classList.toggle("active", added);
      btn.setAttribute("aria-pressed", added ? "true" : "false");

      if (btn.tagName === "BUTTON") {
        btn.textContent = added ? "Added to Compare" : "Add to Compare";
      }
    });
  }

  function syncProductPills() {
    const list = getHandles();

    document.querySelectorAll("[data-product-pill]").forEach((pill) => {
      const handle = pill.getAttribute("data-product-handle") || "";
      const active = list.includes(handle);

      pill.classList.toggle("is-active", active);
      pill.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function syncAll() {
    updateBadges();
    syncCheckboxes();
    syncCompareButtons();
    syncProductPills();
    renderComparePage();
    renderCompareBar();
  }

  function ensureToast() {
    if (document.querySelector("[data-compare-toast]")) return;

    const t = document.createElement("div");
    t.setAttribute("data-compare-toast", "");

    t.innerHTML = `
      <div class="compare-toast__inner" role="status" aria-live="polite">
        <div class="compare-toast__msg" data-compare-toast-msg></div>
        <button type="button" class="compare-toast__close" data-compare-toast-close aria-label="Close">×</button>
      </div>
    `;

    document.body.appendChild(t);

    t.querySelector("[data-compare-toast-close]")?.addEventListener("click", () => {
      t.classList.remove("is-visible");
    });
  }

  let toastTimer = null;

  function showToast(message, ms = 3200) {
    ensureToast();

    const t = document.querySelector("[data-compare-toast]");
    const msgEl = t?.querySelector("[data-compare-toast-msg]");
    if (!t || !msgEl) return;

    msgEl.textContent = message;
    t.classList.add("is-visible");

    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      t.classList.remove("is-visible");
    }, ms);
  }

  function ensureCompareBar() {
    if (isComparePage()) return;
    if (document.querySelector("[data-compare-bar]")) return;

    const bar = document.createElement("div");
    bar.setAttribute("data-compare-bar", "");

    bar.innerHTML = `
      <div class="container">
        <div class="compare-bar__inner">
          <div class="compare-bar__items" data-compare-bar-items></div>

          <div class="compare-bar__right">
            <a class="btn compare-bar__btn" href="${COMPARE_URL}">
              View Compare
              <span class="compare-bar__count" data-compare-bar-count>0</span>
            </a>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(bar);

    bar.addEventListener("click", (e) => {
      const r = e.target.closest("[data-compare-remove]");
      if (!r) return;

      const h = r.getAttribute("data-compare-remove");
      removeProductFromCompare(h);
    });
  }

  async function renderCompareBar() {
    if (isComparePage()) {
      const existing = document.querySelector("[data-compare-bar]");
      if (existing) existing.classList.remove("is-visible");
      return;
    }

    ensureCompareBar();

    const bar = document.querySelector("[data-compare-bar]");
    if (!bar) return;

    const items = read();
    const handles = items.map((item) => item.handle);
    const countEl = bar.querySelector("[data-compare-bar-count]");
    const itemsEl = bar.querySelector("[data-compare-bar-items]");

    if (countEl) countEl.textContent = String(handles.length);

    if (!handles.length) {
      bar.classList.remove("is-visible");
      if (itemsEl) itemsEl.innerHTML = "";
      return;
    }

    bar.classList.add("is-visible");

    if (itemsEl) {
      itemsEl.innerHTML = handles.map((h) => `
        <div class="compare-bar__item" data-compare-bar-item="${escapeHtml(h)}">
          <div class="compare-bar__thumb skeleton"></div>
          <div class="compare-bar__name skeleton"></div>
          <button type="button" class="compare-bar__remove" data-compare-remove="${escapeHtml(h)}" aria-label="Remove">×</button>
        </div>
      `).join("");
    }

    for (const h of handles) {
      const p = await fetchProduct(h);
      if (!p) continue;

      const card = itemsEl?.querySelector(`[data-compare-bar-item="${cssEscape(h)}"]`);
      if (!card) continue;

      const img = p.images && p.images[0] ? p.images[0] : "";
      const thumb = card.querySelector(".compare-bar__thumb");
      const name = card.querySelector(".compare-bar__name");

      if (thumb) {
        thumb.classList.remove("skeleton");

        thumb.innerHTML = img
          ? `<img src="${img}" alt="${escapeHtml(p.title)}" width="80" height="80" loading="lazy">`
          : `<div class="compare-bar__noimg">No image</div>`;
      }

      if (name) {
        name.classList.remove("skeleton");
        name.textContent = p.title || h;
      }
    }
  }

  function sanitizeSpecs(specs) {
    if (!Array.isArray(specs)) return [];

    return specs
      .map((row) => {
        if (!row || typeof row !== "object") return null;

        return {
          group: row.group ? String(row.group) : "",
          label: row.label ? String(row.label) : "",
          value: normalizeCompareValue(row.value)
        };
      })
      .filter(Boolean);
  }

  function getProductVariantCount(p) {
    if (!p || p.has_only_default_variant) return 0;
    if (!Array.isArray(p.variants)) return 0;

    const validVariants = p.variants.filter((variant) => {
      if (!variant) return false;

      const values = [variant.option1, variant.option2, variant.option3].filter(Boolean);

      if (!values.length) return false;
      if (values.length === 1 && values[0] === "Default Title") return false;

      return true;
    });

    return validVariants.length;
  }

  function getOptionsDisplay(p) {
    const variantCount = getProductVariantCount(p);
    return variantCount ? `${variantCount} ${OPTION_FALLBACK_TEXT}` : "-";
  }

  async function addProductToCompare(handle, options = {}) {
    if (!handle) return false;

    let list = read();

    const alreadyAdded = list.some((item) => item.handle === handle);
    if (alreadyAdded) {
      removeProductFromCompare(handle);
      return false;
    }

    if (list.length >= MAX) {
      showToast(`Max ${MAX} products only.`);
      return false;
    }

    const category = options.category || await fetchProductType(handle);
    const categoryTitle = options.categoryTitle || category || "";
    const specs = Array.isArray(options.specs) ? options.specs : [];

    const item = {
      handle,
      category: category || "",
      categoryTitle: categoryTitle || category || "",
      specs: sanitizeSpecs(specs)
    };

    list.push(item);
    write(list);

    clearLegacyCategoryLock();
    syncAll();

    return true;
  }

  function removeProductFromCompare(handle) {
    if (!handle) return;

    const next = read().filter((x) => x.handle !== handle);

    write(next);
    clearLegacyCategoryLock();
    syncAll();
  }

  function clearCompare() {
    write([]);
    clearLegacyCategoryLock();
    syncAll();
  }

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-compare-add]");
    if (!btn) return;

    const handle = btn.getAttribute("data-compare-handle");
    if (!handle) return;

    const list = read();
    const alreadyAdded = list.some((item) => item.handle === handle);

    if (alreadyAdded) {
      removeProductFromCompare(handle);
      return;
    }

    const buttonCategory = btn.getAttribute("data-compare-category") || "";
    const thisType = buttonCategory || await fetchProductType(handle);
    const currentSpecs = getCurrentProductSpecs();

    await addProductToCompare(handle, {
      category: thisType || "",
      categoryTitle: thisType || "",
      specs: currentSpecs
    });
  });

  function getSelectedCategoryData(root) {
    const select = root.querySelector("[data-compare-category-select]");

    if (!select) {
      return {
        handle: "",
        title: "",
        url: ""
      };
    }

    const selectedOption = select.options[select.selectedIndex];

    return {
      handle: select.value || "",
      title: selectedOption?.getAttribute("data-title") || selectedOption?.textContent?.trim() || "",
      url: selectedOption?.getAttribute("data-products-url") || ""
    };
  }

  function renderPickerMessage(root, message) {
    const picker = root.querySelector("[data-compare-product-picker]");
    const pills = root.querySelector("[data-compare-product-pills]");
    const title = root.querySelector("[data-compare-picker-title]");

    if (picker) picker.hidden = false;
    if (title) title.textContent = "Products";

    if (pills) {
      pills.innerHTML = `
        <div class="compare-picker-status">
          ${escapeHtml(message)}
        </div>
      `;
    }
  }

  async function renderCategoryProducts(root) {
    const picker = root.querySelector("[data-compare-product-picker]");
    const pills = root.querySelector("[data-compare-product-pills]");
    const title = root.querySelector("[data-compare-picker-title]");
    const template = root.querySelector("[data-compare-product-pill-template]");

    if (!picker || !pills) return;

    const category = getSelectedCategoryData(root);

    if (!category.handle || !category.url) {
      picker.hidden = true;
      pills.innerHTML = "";
      return;
    }

    picker.hidden = false;

    if (title) {
      title.textContent = category.title ? `${category.title} Products` : "Products";
    }

    renderPickerMessage(root, "Loading products...");

    const products = await fetchCollectionProducts(category.url);

    if (!products.length) {
      renderPickerMessage(root, "No products found in this category.");
      return;
    }

    pills.innerHTML = "";

    products.forEach((product) => {
      if (!product || !product.handle) return;

      let pill = null;

      if (template?.content?.firstElementChild) {
        pill = template.content.firstElementChild.cloneNode(true);
      } else {
        pill = document.createElement("button");
        pill.type = "button";
        pill.className = "compare-product-pill";
        pill.setAttribute("data-product-pill", "");
      }

      ensureProductPillMarkup(pill);

      const productTitle = product.title || product.handle;
      const productImage = getCollectionProductImage(product);
      const productBrand = getCollectionProductBrand(product);

      pill.setAttribute("type", "button");
      pill.setAttribute("data-product-pill", "");
      pill.setAttribute("data-product-handle", product.handle);
      pill.setAttribute("data-product-category", category.handle);
      pill.setAttribute("data-product-category-title", category.title);
      pill.setAttribute("aria-pressed", "false");

      const imageEl = pill.querySelector("[data-product-pill-image]");
      const titleEl = pill.querySelector("[data-product-pill-title]");
      const brandEl = pill.querySelector("[data-product-pill-brand]");

      if (imageEl) {
        if (productImage) {
          imageEl.src = productImage;
          imageEl.alt = productTitle;
          imageEl.hidden = false;
        } else {
          imageEl.removeAttribute("src");
          imageEl.alt = "";
          imageEl.hidden = true;
        }
      }

      if (titleEl) {
        titleEl.textContent = productTitle;
      }

      if (brandEl) {
        brandEl.textContent = productBrand || "No brand";
      }

      pills.appendChild(pill);
    });

    syncProductPills();
  }

  function initComparePageCategoryPicker() {
    const root = document.querySelector("[data-compare-page]");
    if (!root || root.__categoryPickerBound) return;

    root.__categoryPickerBound = true;

    const select = root.querySelector("[data-compare-category-select]");
    const clear = root.querySelector("[data-compare-clear]");

    if (select) {
      select.addEventListener("change", () => {
        renderCategoryProducts(root);
      });

      if (select.value) {
        renderCategoryProducts(root);
      }
    }

    if (clear) {
      clear.addEventListener("click", () => {
        clearCompare();
      });
    }

    root.addEventListener("click", async (e) => {
      const removeBtn = e.target.closest("[data-compare-remove]");
      if (removeBtn) {
        const h = removeBtn.getAttribute("data-compare-remove");
        removeProductFromCompare(h);
        return;
      }

      const pill = e.target.closest("[data-product-pill]");
      if (!pill) return;

      const handle = pill.getAttribute("data-product-handle") || "";
      if (!handle) return;

      const list = read();
      const alreadyAdded = list.some((item) => item.handle === handle);

      if (alreadyAdded) {
        removeProductFromCompare(handle);
        return;
      }

      const category = getSelectedCategoryData(root);
      const specs = await fetchProductSpecsFromPage(handle);

      await addProductToCompare(handle, {
        category: category.handle || pill.getAttribute("data-product-category") || "",
        categoryTitle: category.title || pill.getAttribute("data-product-category-title") || "",
        specs
      });
    });
  }

  async function renderComparePage() {
    const root = document.querySelector("[data-compare-page]");
    if (!root) return;

    const empty = root.querySelector(".compare-empty");
    const wrap = root.querySelector(".compare-table-wrap");
    const head = root.querySelector("[data-compare-head]");
    const body = root.querySelector("[data-compare-body]");

    const storedItems = read();
    const handles = storedItems.map((item) => item.handle);

    syncProductPills();

    if (!handles.length) {
      if (empty) empty.hidden = false;
      if (wrap) wrap.hidden = true;
      if (head) head.innerHTML = "";
      if (body) body.innerHTML = "";
      return;
    }

    const products = [];

    for (const h of handles) {
      const p = await fetchProduct(h);

      if (p) {
        const extra = getStoredCompareItem(h);
        p.__compareSpecs = Array.isArray(extra?.specs) ? extra.specs : [];
        p.__selectedCategory = extra?.category || "";
        p.__selectedCategoryTitle = extra?.categoryTitle || "";
        products.push(p);
      }
    }

    if (!products.length) {
      if (empty) empty.hidden = false;
      if (wrap) wrap.hidden = true;
      if (head) head.innerHTML = "";
      if (body) body.innerHTML = "";
      return;
    }

    if (empty) empty.hidden = true;
    if (wrap) wrap.hidden = false;

    if (head) {
      head.innerHTML = `<th class="compare-field-col">Field</th>` + products.map((p) => {
        const img = p.images && p.images[0] ? p.images[0] : "";
        const title = escapeHtml(p.title || "");
        const url = p.url || `/products/${p.handle}`;

        const v0 = p.variants && p.variants[0] ? p.variants[0] : null;
        const price = v0?.price ?? p.price ?? 0;
        const compare = v0?.compare_at_price ?? 0;

        const priceHtml = `
          <span class="product-card__price">${formatMoney(price)}</span>
          ${compare && compare > price ? `<span class="product-card__compare">${formatMoney(compare)}</span>` : ""}
        `;

        return `
          <th class="compare-product-col">
            <article class="product-card compare-product-card">
              <a class="product-card__media-wrap" href="${url}">
                ${img ? `<img class="product-card__media" src="${img}" alt="${title}" width="300" height="300" loading="lazy">` : ""}
              </a>

              <div class="product-card__content">
                <h3 class="product-card__title">
                  <a href="${url}">${title}</a>
                </h3>

                <div class="product-card__price-wrap">
                  ${priceHtml}
                </div>

                <button type="button" class="compare-remove product-card__remove" data-compare-remove="${escapeHtml(p.handle)}">
                  Remove
                </button>
              </div>
            </article>
          </th>
        `;
      }).join("");
    }

    const baseRows = [
      // ["Category", (p) => p.__selectedCategoryTitle || p.__selectedCategory || "-"],
      ["Category", () => "Reformers"],
      // ["Product Type", (p) => p.type || "-"],
      ["Brand", (p) => p.vendor || "-"],
      // ["Available", (p) => (p.available ? "Yes" : "No")],
      ["Price", (p) => {
        const v0 = p.variants?.[0];
        const price = v0?.price ?? p.price ?? 0;
        return formatMoney(price);
      }],
      // ["Compare price", (p) => {
      //   const v0 = p.variants?.[0];
      //   const price = v0?.price ?? p.price ?? 0;
      //   const compare = v0?.compare_at_price ?? 0;
      //   return compare && compare > price ? formatMoney(compare) : "-";
      // }],
      // ["Sale %", (p) => {
      //   const v0 = p.variants?.[0];
      //   const price = Number(v0?.price ?? p.price ?? 0);
      //   const compare = Number(v0?.compare_at_price ?? 0);

      //   if (!compare || compare <= price) return "-";

      //   return `${Math.round(((compare - price) * 100) / compare)}%`;
      // }],
      // ["SKU", (p) => (p.variants?.[0]?.sku || "-")],
      ["Weight", (p) => (p.variants?.[0]?.weight ? `${p.variants[0].weight}g` : "-")],
      ["Summary", (p) => {
        const txt = (p.description || "").replace(/<[^>]*>/g, "").trim();
        return txt ? (txt.length > 160 ? txt.slice(0, 160) + "..." : txt) : "-";
      }],
      ["Options", (p) => getOptionsDisplay(p)]
    ];

    const preferredSpecOrder = [
      "Usage Warranty",
      "Springs",
      "Frame",
      "Footbar",
      "Leg & Height Options",
      "Tower/Cadillac",
      "Storage",
      "Rope Adjustment System",
      "Dimensions/Weight",
      "Carriage Size",
      "Travel Length",
      "Spring Bar & Spring Bar Adjustment",
      "Off Centred Shoulder Rests",
      "Foot Bar Size",
      "Adjustable Feet",
      "Runners/Wheels",
      "Maximum User Weight",
      "Rope Risers",
      "Folding/Standing/Stacking",
      "Items Included with Bundle",
      "Optional Items",
    ];

    const specLabels = [];

    preferredSpecOrder.forEach((label) => {
      const exists = products.some((p) =>
        (p.__compareSpecs || []).some((row) => row && row.label === label)
      );

      if (exists) specLabels.push(label);
    });

    products.forEach((p) => {
      (p.__compareSpecs || []).forEach((row) => {
        if (row && row.label && !specLabels.includes(row.label)) {
          specLabels.push(row.label);
        }
      });
    });

    const specRows = specLabels.map((label) => [
      label,
      (p) => {
        const row = (p.__compareSpecs || []).find((r) => r.label === label);
        return row ? row.value : "-";
      }
    ]);

    const allRows = [...baseRows, ...specRows];

    if (body) {
      body.innerHTML = allRows.map(([label, fn]) => `
        <tr>
          <td><strong>${escapeHtml(String(label))}</strong></td>
          ${products.map((p) => `<td>${formatCompareCell(fn(p))}</td>`).join("")}
        </tr>
      `).join("");
    }

    syncProductPills();
  }

  function formatMoney(cents) {
    const v = (Number(cents) || 0) / 100;

    const currency =
      window.Shopify && window.Shopify.currency && window.Shopify.currency.active
        ? window.Shopify.currency.active
        : "AED";

    return v.toLocaleString(undefined, {
      style: "currency",
      currency
    });
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (m) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;"
    }[m]));
  }

  function cssEscape(str) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(String(str));
    }

    return String(str).replace(/["\\]/g, "\\$&");
  }

  function normalizeCompareValue(value) {
    if (value === null || value === undefined || value === "") return "-";

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }

    if (Array.isArray(value)) {
      const arr = value
        .map((item) => normalizeCompareValue(item))
        .filter((item) => item !== "-" && item !== "");

      return arr.length ? arr.join(", ") : "-";
    }

    if (typeof value === "object") {
      if (value.name && Array.isArray(value.values)) {
        return `${value.name}: ${value.values.join(", ")}`;
      }

      const pairs = Object.keys(value)
        .map((key) => {
          let v = value[key];

          if (Array.isArray(v)) {
            v = v.map((item) => normalizeCompareValue(item)).join(", ");
          } else if (v && typeof v === "object") {
            v = normalizeCompareValue(v);
          } else if (v === null || v === undefined) {
            v = "";
          } else {
            v = String(v);
          }

          return v ? `${key}: ${v}` : "";
        })
        .filter(Boolean);

      return pairs.length ? pairs.join(", ") : "-";
    }

    return String(value);
  }

  function formatCompareCell(value) {
    const normalized = normalizeCompareValue(value);
    if (normalized === "-" || normalized === "") return "-";

    return escapeHtml(normalized)
      .replace(/&lt;br\s*\/?&gt;/gi, "<br>")
      .replace(/\r\n|\r|\n/g, "<br>");
  }

  function init() {
    clearLegacyCategoryLock();
    initComparePageCategoryPicker();

    updateBadges();
    syncCheckboxes();
    syncCompareButtons();
    syncProductPills();
    renderComparePage();
    renderCompareBar();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();